'use client';

import { FacebookIcon } from '@/components/organisms/SocialIconGroup/FacebookIcon';
import { LinkedInIcon } from '@/components/organisms/SocialIconGroup/LinkedInIcon';
import { TwitterIcon } from '@/components/organisms/SocialIconGroup/TwitterIcon';
import { showToast } from '@/lib/toast';
import { LinkIcon } from '@heroicons/react/24/outline';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

interface ShareBlogProps {
  containerStyle?: string;
}

export const ShareBlog = ({ containerStyle }: ShareBlogProps) => {
  const [url, setUrl] = useState('');

  const socialMediaLinks: {
    href: string;
    icon: ReactElement;
    ariaLabel: string;
  }[] = useMemo(
    () => [
      {
        href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          url
        )}`,
        ariaLabel: 'facebook',
        icon: <FacebookIcon className="size-2xl text-white lg:size-xl" />,
      },
      {
        href: `//twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
        ariaLabel: 'twitter',
        icon: <TwitterIcon className="size-2xl text-white lg:size-xl" />,
      },
      {
        href: `//linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          url
        )}`,
        ariaLabel: 'linkedin',
        icon: <LinkedInIcon className="size-2xl text-white lg:size-xl" />,
      },
    ],
    [url]
  );

  const copyCurrentUrlToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      showToast('Link copied to clipboard', { type: 'success' });
    } catch (error) {
      showToast('Failed to copy link to clipboard', { type: 'error' });
    }
  }, [url]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setUrl(window.location.href); // Full URL with protocol, hostname, and path
    }
  }, []);

  return (
    <section className={containerStyle}>
      {socialMediaLinks.map(({ href, icon, ariaLabel }, index) => (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noreferrer"
          aria-label={ariaLabel}
        >
          {icon}
        </a>
      ))}
      <button
        onClick={copyCurrentUrlToClipboard}
        aria-label="copy article link to clipboard"
      >
        <LinkIcon className="size-2xl text-white lg:size-xl" />
      </button>
    </section>
  );
};
